/**
 * Stop Game Validator & Scoring Service
 * Validates words against English dictionary APIs, verifies category relevance,
 * detects spelling mistakes with small point penalties, provides educational word suggestions,
 * and calculates competitive STOP points.
 */

import { getCategorySuggestions, CATEGORY_VOCABULARY } from './stopGameSuggestions';
export { getCategorySuggestions };

export interface StopCategory {
  id: string;
  name: string;
  description: string;
  placeholder: string;
  icon: string;
}

export interface WordValidationResult {
  isValid: boolean;
  word?: string;
  reason?: string;
  definition?: string;
  phonetic?: string;
  audio?: string;
  partOfSpeech?: string;
  example?: string;
  isTypo?: boolean;
  correctedWord?: string;
  penalty?: number;
  suggestedExamples?: string[];
}

export const DEFAULT_STOP_CATEGORIES: StopCategory[] = [
  {
    id: 'animal',
    name: 'Animal',
    description: 'Any living animal, bird, fish, insect, or creature',
    placeholder: 'Enter an animal...',
    icon: '🐾'
  },
  {
    id: 'food',
    name: 'Food & Drink',
    description: 'Any edible item, fruit, meal, vegetable, or beverage',
    placeholder: 'Enter a food or drink...',
    icon: '🍎'
  },
  {
    id: 'country_city',
    name: 'Country or City',
    description: 'Any country, city, or major geographical territory',
    placeholder: 'Enter a country or city...',
    icon: '🌍'
  },
  {
    id: 'job',
    name: 'Job / Profession',
    description: 'Any career, occupation, or trade',
    placeholder: 'Enter a job or profession...',
    icon: '💼'
  },
  {
    id: 'object',
    name: 'Object or Thing',
    description: 'Any physical object, tool, vehicle, or everyday item',
    placeholder: 'Enter an object or item...',
    icon: '📦'
  },
  {
    id: 'verb',
    name: 'Verb (Action)',
    description: 'Any action or state verb in base form',
    placeholder: 'Enter an action verb...',
    icon: '⚡'
  },
  {
    id: 'adjective',
    name: 'Adjective',
    description: 'Any descriptive word',
    placeholder: 'Enter an adjective...',
    icon: '✨'
  },
  {
    id: 'color',
    name: 'Color',
    description: 'Any color, shade, or hue',
    placeholder: 'Enter a color...',
    icon: '🎨'
  },
  {
    id: 'sport',
    name: 'Sport or Game',
    description: 'Any athletic sport, recreation, or competitive game',
    placeholder: 'Enter a sport or game...',
    icon: '⚽'
  },
  {
    id: 'clothing',
    name: 'Clothing & Accessory',
    description: 'Any garment, clothing item, shoe, or wearable accessory',
    placeholder: 'Enter a clothing item...',
    icon: '👕'
  },
  {
    id: 'body_part',
    name: 'Body Part / Anatomy',
    description: 'Any human or animal body part, organ, bone, or muscle',
    placeholder: 'Enter a body part...',
    icon: '🫀'
  },
  {
    id: 'musical_instrument',
    name: 'Musical Instrument',
    description: 'Any musical instrument, acoustic device, or apparatus',
    placeholder: 'Enter an instrument...',
    icon: '🎸'
  },
  {
    id: 'vehicle',
    name: 'Vehicle / Transportation',
    description: 'Any vehicle, means of transport, automobile, boat, or aircraft',
    placeholder: 'Enter a vehicle...',
    icon: '🚗'
  },
  {
    id: 'plant_flower',
    name: 'Plant or Flower',
    description: 'Any tree, flower, plant, shrub, or botanical flora',
    placeholder: 'Enter a plant or flower...',
    icon: '🌸'
  },
  {
    id: 'school_subject',
    name: 'School Subject / Science',
    description: 'Any academic discipline, field of study, or science',
    placeholder: 'Enter a school subject...',
    icon: '📚'
  },
  {
    id: 'movie_tv',
    name: 'Movie or TV Show',
    description: 'Any film, cinema title, television show, or cartoon',
    placeholder: 'Enter a movie or show...',
    icon: '🎬'
  },
  {
    id: 'celebrity',
    name: 'Famous Person / Celebrity',
    description: 'Any real celebrity, artist, actor, singer, or notable figure',
    placeholder: 'Enter a famous person...',
    icon: '🌟'
  },
  {
    id: 'brand',
    name: 'Brand or Company',
    description: 'Any well-known commercial brand, logo, or company',
    placeholder: 'Enter a brand or company...',
    icon: '🏷️'
  }
];

// In-memory cache for API lookups to ensure ultra-fast validation
const validationCache = new Map<string, WordValidationResult>();

/**
 * Computes Levenshtein edit distance between two strings
 */
export function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
}

/**
 * Fetch with an internal timeout to avoid hanging on 522 Cloudflare issues.
 */
async function fetchWithTimeout(url: string, fetchFn: typeof fetch, ms: number = 3000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetchFn(url, { signal: controller.signal });
    clearTimeout(timer);
    return res;
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }
}

/**
 * Validates that a given word starts with the specified target letter.
 */
export function validateWordStartsWithLetter(word: string, letter: string): boolean {
  if (!word || !letter) return false;
  const cleanWord = word.trim().toLowerCase();
  const cleanLetter = letter.trim().toLowerCase();
  if (cleanWord.length === 0 || cleanLetter.length === 0) return false;
  return cleanWord.startsWith(cleanLetter);
}

/**
 * Validates a word using English dictionary APIs.
 * Supports exact matching, typo/spelling mistake detection with small penalty,
 * and educational word suggestions for blank/unsubmitted entries.
 */
/**
 * Checks if a word and its definition/part-of-speech make sense for a given category.
 */
export function isCategoryRelevant(
  word: string,
  definition: string,
  partOfSpeech: string,
  categoryId: string,
  letter?: string
): { isRelevant: boolean; reason?: string } {
  const cleanWord = word.trim().toLowerCase();
  const cleanPos = partOfSpeech ? partOfSpeech.trim().toLowerCase() : '';
  const cleanDef = definition ? definition.toLowerCase() : '';
  const upperLetter = letter ? letter.trim().toUpperCase() : cleanWord.charAt(0).toUpperCase();

  // 1. Curated vocabulary check: If known in category, it's 100% relevant
  const curated = CATEGORY_VOCABULARY[categoryId]?.[upperLetter] || [];
  if (curated.some(w => w.toLowerCase() === cleanWord)) {
    return { isRelevant: true };
  }

  // 2. Specific Category rules
  switch (categoryId) {
    case 'verb': {
      // Must be a verb
      const isVerbPos = cleanPos === 'v' || cleanPos.includes('verb');
      const isVerbDef = cleanDef.startsWith('to ') || cleanDef.includes('to act') || cleanDef.includes('to move');
      if (!isVerbPos && !isVerbDef && cleanPos && !cleanPos.includes('v')) {
        return { isRelevant: false, reason: `"${word}" is not a verb` };
      }
      return { isRelevant: true };
    }

    case 'adjective': {
      // Must be an adjective
      const isAdjPos = cleanPos === 'adj' || cleanPos === 'j' || cleanPos.includes('adjective');
      if (!isAdjPos && cleanPos && (cleanPos === 'v' || cleanPos === 'n' || cleanPos === 'noun')) {
        return { isRelevant: false, reason: `"${word}" is not an adjective` };
      }
      return { isRelevant: true };
    }

    case 'object': {
      // Physical object or thing: Must NOT be an exclusively pure action verb or adjective
      const isPureVerb = cleanPos === 'v' && (cleanDef.startsWith('to ') || cleanDef.includes('to move') || cleanDef.includes('to perform'));
      if (isPureVerb) {
        return { isRelevant: false, reason: `"${word}" is an action verb, not a physical object or thing` };
      }
      const isPureAdj = (cleanPos === 'adj' || cleanPos === 'j') && !cleanDef.includes('artifact') && !cleanDef.includes('substance');
      if (isPureAdj) {
        return { isRelevant: false, reason: `"${word}" is an adjective, not an object or thing` };
      }
      return { isRelevant: true };
    }

    case 'animal': {
      // Animal keywords in definition
      const animalRegex = /\b(animal|mammal|bird|fish|insect|reptile|amphibian|species|genus|breed|creature|carnivore|herbivore|rodent|canine|feline|crustacean|mollusk|arachnid|worm|organism|marsupial|primate|cetacean|poultry|wildlife|livestock|fauna|predator|prey|puppy|kitten|hound|duck|goose|hen|rooster|lamb|calf|piglet|foal|snake|lizard|frog|toad|ant|bee|moth|beetle|spider|shark|whale|seal|bear|deer|wolf|fox|badger|otter|beaver|bat|owl|hawk|eagle)\b/i;
      const nonAnimalMedicalRegex = /\b(\(medicine\)|medical|disease|illness|feeling of cold|shivering|fever|symptom|syndrome|infection|treatment|surgery)\b/i;

      if (nonAnimalMedicalRegex.test(cleanDef) && !animalRegex.test(cleanDef)) {
        return { isRelevant: false, reason: `"${word}" is a medical/scientific term, not an animal` };
      }
      if (cleanPos === 'v' && !animalRegex.test(cleanDef)) {
        return { isRelevant: false, reason: `"${word}" is a verb, not an animal` };
      }
      if (cleanDef && !animalRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(city|country|state|town|concept|philosophy|feeling|emotion|machine|software|device)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as an animal` };
        }
      }
      return { isRelevant: true };
    }

    case 'food': {
      const foodRegex = /\b(food|dish|fruit|vegetable|beverage|drink|edible|meat|culinary|ingredient|meal|cuisine|snack|dessert|cereal|herb|spice|condiment|flavour|flavor|bread|pasta|cheese|dairy|soup|pastry|grain|berry|wine|beer|tea|coffee|juice|seed|nut|sauce|stew|salad|cake|pie|candy|sweet|confection|liquor|plant|crop|root)\b/i;
      if (cleanPos === 'v' && cleanDef.startsWith('to ')) {
        return { isRelevant: false, reason: `"${word}" is a verb, not a food or drink` };
      }
      if (cleanDef && !foodRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(machine|tool|software|vehicle|animal species|city|country|feeling)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a food or drink` };
        }
      }
      return { isRelevant: true };
    }

    case 'job': {
      const jobRegex = /\b(agent noun|person who|one who|someone who|occupation|profession|job|trade|worker|officer|specialist|practitioner|doctor|engineer|artist|teacher|player|driver|manager|clerk|attendant|employee|leader|performer|scientist|lawyer|judge|nurse|cook|chef|pilot|mechanic|writer|author|actor|actress|musician|singer|dancer|soldier|sailor|police|guard|craftsman|artisan|baker|builder|clergyman|merchant|tailor|butcher|carpenter|electrician|plumber|title|vocation|career)\b/i;
      if (cleanPos === 'v' && cleanDef.startsWith('to ')) {
        return { isRelevant: false, reason: `"${word}" is an action verb, not a profession` };
      }
      if (cleanDef && !jobRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(animal|food|fruit|vegetable|tool|device|vehicle|city|country)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a job or profession` };
        }
      }
      return { isRelevant: true };
    }

    case 'color': {
      const colorRegex = /\b(color|colour|shade|hue|pigment|tint|dye|chroma)\b/i;
      if (cleanDef && !colorRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (!/\b(red|blue|green|yellow|black|white|purple|pink|orange|brown|grey|gray|violet|indigo|cyan|magenta|amber|gold|silver|bronze|turquoise|teal|beige|maroon|coral|crimson|ruby|emerald|sapphire|ivory|navy)\b/i.test(cleanWord)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a color` };
        }
      }
      return { isRelevant: true };
    }

    case 'sport': {
      const sportRegex = /\b(sport|game|athletics|exercise|competition|tournament|match|martial art|gymnastics|racing|championship|ball game|recreation|athletic)\b/i;
      if (cleanDef && !sportRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(chemical|organism|anatomy|country|city)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a sport or game` };
        }
      }
      return { isRelevant: true };
    }

    case 'clothing': {
      const clothingRegex = /\b(clothing|garment|apparel|wear|dress|shirt|pants|jacket|coat|shoes|footwear|hat|glove|accessory|trousers|skirt|suit|underwear|costume|fabric|headwear)\b/i;
      if (cleanDef && !clothingRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(animal species|country|city|abstract|verb|concept)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a clothing item` };
        }
      }
      return { isRelevant: true };
    }

    case 'body_part': {
      const bodyRegex = /\b(anatomy|body|organ|bone|muscle|limb|joint|tissue|head|face|eye|ear|arm|leg|foot|hand|finger|heart|brain|skin|blood|nerve|artery|vein|skeleton)\b/i;
      if (cleanDef && !bodyRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(machine|vehicle|tool|software|country|city)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a body part` };
        }
      }
      return { isRelevant: true };
    }

    case 'musical_instrument': {
      const instRegex = /\b(musical instrument|instrument|music|string instrument|wind instrument|percussion|keyboard|brass|drum|guitar|piano|flute|violin|orchestra|horn|sound)\b/i;
      if (cleanDef && !instRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(animal species|country|city|medical)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a musical instrument` };
        }
      }
      return { isRelevant: true };
    }

    case 'vehicle': {
      const vehRegex = /\b(vehicle|car|automobile|transport|transportation|aircraft|plane|airplane|ship|boat|train|bicycle|bike|motorcycle|truck|bus|vessel|craft|submarine)\b/i;
      if (cleanDef && !vehRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(food|plant|flower|animal species|clothing)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a vehicle` };
        }
      }
      return { isRelevant: true };
    }

    case 'plant_flower': {
      const plantRegex = /\b(plant|flower|tree|shrub|herb|flora|botany|leaf|petal|stem|bark|blossom|bloom|botanical|vegetation|seed|grass|moss|fern|crop|root)\b/i;
      if (cleanDef && !plantRegex.test(cleanDef) && cleanDef.length > 20 && !cleanDef.includes('lexical database')) {
        if (/\b(machine|tool|software|vehicle|country|city)\b/i.test(cleanDef)) {
          return { isRelevant: false, reason: `"${word}" is not recognized as a plant or flower` };
        }
      }
      return { isRelevant: true };
    }

    case 'school_subject': {
      return { isRelevant: true };
    }

    case 'movie_tv':
    case 'celebrity':
    case 'brand': {
      return { isRelevant: true };
    }

    case 'country_city': {
      return { isRelevant: true };
    }

    default:
      return { isRelevant: true };
  }
}

/**
 * Validates a word using English dictionary APIs.
 * Supports exact matching, category semantic verification,
 * typo/spelling mistake detection with 8-point penalty,
 * and educational word suggestions for blank/unsubmitted entries.
 */
export async function validateWordWithApi(
  word: string,
  letter: string,
  categoryId: string,
  fetchFn: typeof fetch = fetch
): Promise<WordValidationResult> {
  const cleanWord = word ? word.trim().toLowerCase() : '';
  const cleanLetter = letter ? letter.trim().toLowerCase() : '';

  if (!cleanWord) {
    return {
      isValid: false,
      reason: 'Word is empty',
      suggestedExamples: getCategorySuggestions(letter, categoryId)
    };
  }

  if (!cleanWord.startsWith(cleanLetter)) {
    return {
      isValid: false,
      reason: `Must start with "${cleanLetter.toUpperCase()}"`,
      suggestedExamples: getCategorySuggestions(letter, categoryId)
    };
  }

  // Check cache
  const cacheKey = `${cleanWord}:${categoryId}:${cleanLetter}`;
  if (validationCache.has(cacheKey)) {
    return validationCache.get(cacheKey)!;
  }

  // 1. PRIMARY: Try Datamuse API first (ultra-fast, supports proper nouns/places like Tokyo, no 522)
  try {
    const datamuseRes = await fetchWithTimeout(
      `https://api.datamuse.com/words?sp=${encodeURIComponent(cleanWord)}&md=dp&max=1`,
      fetchFn,
      3000
    );

    if (datamuseRes.ok) {
      const datamuseData = await datamuseRes.json();
      if (
        Array.isArray(datamuseData) &&
        datamuseData.length > 0 &&
        datamuseData[0].word.toLowerCase() === cleanWord
      ) {
        const item = datamuseData[0];
        const rawDef = item.defs?.[0] || '';
        let partOfSpeech = '';
        let definition = '';
        if (rawDef) {
          const parts = rawDef.split('\t');
          if (parts.length === 2) {
            partOfSpeech = parts[0];
            definition = parts[1];
          } else {
            definition = rawDef;
          }
        } else if (item.meanings?.[0]?.definitions?.[0]?.definition) {
          definition = item.meanings[0].definitions[0].definition;
          partOfSpeech = item.meanings[0].partOfSpeech || '';
        } else {
          definition = 'English word found in lexical database';
        }

        // Verify Category Relevance
        const relevance = isCategoryRelevant(cleanWord, definition, partOfSpeech, categoryId, cleanLetter);
        if (!relevance.isRelevant) {
          const invalidCategoryResult: WordValidationResult = {
            isValid: false,
            word: cleanWord,
            definition,
            partOfSpeech,
            reason: relevance.reason,
            suggestedExamples: getCategorySuggestions(letter, categoryId)
          };
          validationCache.set(cacheKey, invalidCategoryResult);
          return invalidCategoryResult;
        }

        const result: WordValidationResult = {
          isValid: true,
          word: cleanWord,
          definition,
          partOfSpeech
        };

        validationCache.set(cacheKey, result);
        return result;
      }
    }
  } catch (e) {
    console.warn(`Datamuse check skipped for "${cleanWord}":`, e);
  }

  // 2. SECONDARY: freedictionaryapi.com (Reliable, fast Wiktionary-backed API)
  try {
    const freeDictRes = await fetchWithTimeout(
      `https://freedictionaryapi.com/api/v1/entries/en/${encodeURIComponent(cleanWord)}`,
      fetchFn,
      2500
    );
    if (freeDictRes.ok) {
      const data = await freeDictRes.json();
      if (data && data.entries && data.entries.length > 0) {
        const firstEntry = data.entries[0];
        const firstSense = firstEntry?.senses?.[0];
        const definition = firstSense?.definition || 'English dictionary entry';
        const partOfSpeech = firstEntry.partOfSpeech || '';

        // Verify Category Relevance
        const relevance = isCategoryRelevant(cleanWord, definition, partOfSpeech, categoryId, cleanLetter);
        if (!relevance.isRelevant) {
          const invalidCategoryResult: WordValidationResult = {
            isValid: false,
            word: cleanWord,
            definition,
            partOfSpeech,
            reason: relevance.reason,
            suggestedExamples: getCategorySuggestions(letter, categoryId)
          };
          validationCache.set(cacheKey, invalidCategoryResult);
          return invalidCategoryResult;
        }

        const result: WordValidationResult = {
          isValid: true,
          word: cleanWord,
          definition,
          partOfSpeech
        };
        validationCache.set(cacheKey, result);
        return result;
      }
    }
  } catch (error) {
    console.warn(`FreeDictionaryAPI skipped or errored for "${cleanWord}":`, error);
  }

  // 3. TERTIARY: Wikimedia Wiktionary REST API (High availability, no API key required)
  try {
    const wikiRes = await fetchWithTimeout(
      `https://en.wiktionary.org/api/rest_v1/page/definition/${encodeURIComponent(cleanWord)}`,
      fetchFn,
      2500
    );
    if (wikiRes.ok) {
      const wikiData = await wikiRes.json();
      if (wikiData && wikiData.en && wikiData.en.length > 0) {
        const firstEntry = wikiData.en[0];
        const rawDef = firstEntry.definitions?.[0]?.definition || '';
        const definition = rawDef.replace(/<[^>]*>/g, '').trim() || 'English dictionary entry';
        const partOfSpeech = (firstEntry.partOfSpeech || '').toLowerCase();

        // Verify Category Relevance
        const relevance = isCategoryRelevant(cleanWord, definition, partOfSpeech, categoryId, cleanLetter);
        if (!relevance.isRelevant) {
          const invalidCategoryResult: WordValidationResult = {
            isValid: false,
            word: cleanWord,
            definition,
            partOfSpeech,
            reason: relevance.reason,
            suggestedExamples: getCategorySuggestions(letter, categoryId)
          };
          validationCache.set(cacheKey, invalidCategoryResult);
          return invalidCategoryResult;
        }

        const result: WordValidationResult = {
          isValid: true,
          word: cleanWord,
          definition,
          partOfSpeech
        };
        validationCache.set(cacheKey, result);
        return result;
      }
    }
  } catch (error) {
    console.warn(`Wiktionary REST API skipped or errored for "${cleanWord}":`, error);
  }

  // 4. QUATERNARY: Try dictionaryapi.dev with timeout (Handles 522/downtime gracefully)
  try {
    const response = await fetchWithTimeout(
      `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(cleanWord)}`,
      fetchFn,
      2000
    );

    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        const entry = data[0];
        const phonetic = entry.phonetic || entry.phonetics?.find((p: any) => p?.text)?.text || '';
        const audio = entry.phonetics?.find((p: any) => p?.audio && p.audio.trim().length > 0)?.audio || '';
        const firstMeaning = entry.meanings?.[0];
        const partOfSpeech = firstMeaning?.partOfSpeech || '';
        const defObj = firstMeaning?.definitions?.[0];
        const definition = defObj?.definition || 'English dictionary entry';
        const example = defObj?.example || '';

        // Verify Category Relevance
        const relevance = isCategoryRelevant(cleanWord, definition, partOfSpeech, categoryId, cleanLetter);
        if (!relevance.isRelevant) {
          const invalidCategoryResult: WordValidationResult = {
            isValid: false,
            word: cleanWord,
            definition,
            partOfSpeech,
            phonetic,
            audio,
            reason: relevance.reason,
            suggestedExamples: getCategorySuggestions(letter, categoryId)
          };
          validationCache.set(cacheKey, invalidCategoryResult);
          return invalidCategoryResult;
        }

        const result: WordValidationResult = {
          isValid: true,
          word: cleanWord,
          definition,
          phonetic,
          audio,
          partOfSpeech,
          example
        };

        validationCache.set(cacheKey, result);
        return result;
      }
    }
  } catch (error) {
    console.warn(`dictionaryapi.dev timed out or returned 522 for "${cleanWord}":`, error);
  }

  // 4. SPELLING MISTAKE / TYPO DETECTION
  // Check if this is a minor spelling mistake of a valid English word starting with target letter that fits the category
  try {
    const candidateWords: string[] = [];

    // Collect candidates from curated category dictionary (verified to fit category!)
    const categoryWords = CATEGORY_VOCABULARY[categoryId]?.[cleanLetter.toUpperCase()] || [];
    candidateWords.push(...categoryWords);

    // Map category to Datamuse topic
    const topicMap: Record<string, string> = {
      animal: 'animals',
      food: 'food',
      job: 'jobs',
      country_city: 'geography',
      color: 'colors'
    };
    const topicParam = topicMap[categoryId] ? `&topics=${topicMap[categoryId]}` : '';

    // Also query Datamuse suggestions / sound-alike with topic constraint
    try {
      const sugRes = await fetchWithTimeout(
        `https://api.datamuse.com/sug?s=${encodeURIComponent(cleanWord)}${topicParam}`,
        fetchFn,
        2000
      );
      if (sugRes.ok) {
        const sugData = await sugRes.json();
        if (Array.isArray(sugData)) {
          for (const s of sugData) {
            if (s.word && s.word.toLowerCase().startsWith(cleanLetter)) {
              candidateWords.push(s.word);
            }
          }
        }
      }
    } catch {
      // ignore
    }

    try {
      const slRes = await fetchWithTimeout(
        `https://api.datamuse.com/words?sl=${encodeURIComponent(cleanWord)}${topicParam}`,
        fetchFn,
        2000
      );
      if (slRes.ok) {
        const slData = await slRes.json();
        if (Array.isArray(slData)) {
          for (const s of slData) {
            if (s.word && s.word.toLowerCase().startsWith(cleanLetter)) {
              candidateWords.push(s.word);
            }
          }
        }
      }
    } catch {
      // ignore
    }

    // Find best match with edit distance <= 2 that passes category relevance
    let bestCandidate = '';
    let bestCandidateDef = '';
    let bestCandidatePos = '';
    let minDistance = 99;

    for (const cand of candidateWords) {
      const candClean = cand.trim().toLowerCase();
      if (candClean === cleanWord) continue;
      if (!candClean.startsWith(cleanLetter)) continue;
      const dist = levenshteinDistance(cleanWord, candClean);
      const allowedDistance = cleanWord.length >= 8 ? 3 : 2;
      if (dist <= allowedDistance && dist < minDistance) {
        // Fetch definition and check if it fits category
        let candDef = `Spelling correction for "${candClean}"`;
        let candPos = '';
        try {
          const defRes = await fetchWithTimeout(
            `https://api.datamuse.com/words?sp=${encodeURIComponent(candClean)}&md=dp&max=1`,
            fetchFn,
            2000
          );
          if (defRes.ok) {
            const dData = await defRes.json();
            if (Array.isArray(dData) && dData[0]?.defs?.[0]) {
              const parts = dData[0].defs[0].split('\t');
              if (parts.length === 2) {
                candPos = parts[0];
                candDef = parts[1];
              } else {
                candDef = dData[0].defs[0];
              }
            }
          }
        } catch {
          // ignore
        }

        const relevance = isCategoryRelevant(candClean, candDef, candPos, categoryId, cleanLetter);
        if (relevance.isRelevant) {
          minDistance = dist;
          bestCandidate = candClean;
          bestCandidateDef = candDef;
          bestCandidatePos = candPos;
        }
      }
    }

    if (bestCandidate) {
      const typoResult: WordValidationResult = {
        isValid: true,
        isTypo: true,
        word: cleanWord,
        correctedWord: bestCandidate,
        penalty: 0,
        reason: `Potential spelling error: Did you mean "${bestCandidate}"?`,
        definition: bestCandidateDef,
        partOfSpeech: bestCandidatePos
      };

      validationCache.set(cacheKey, typoResult);
      return typoResult;
    }
  } catch (err) {
    console.warn(`Typo detection error for "${cleanWord}":`, err);
  }

  const failedResult: WordValidationResult = {
    isValid: false,
    reason: 'Not found in English dictionary',
    suggestedExamples: getCategorySuggestions(letter, categoryId)
  };
  validationCache.set(cacheKey, failedResult);
  return failedResult;
}

/**
 * Calculates standard STOP scores across all players and categories:
 * - 10 points: Valid unique word
 * - 5 points: Valid word, duplicate with another player
 * - Deducts 8-point penalty for spelling mistakes (isTypo) -> +2 pts for unique
 * - Disagreement & Peer Voting Scoring Rules:
 *   - Only lose points if someone votes disagreeing on their words.
 *   - If everyone disagrees (100% of other players): 0 points (lose all points).
 *   - If most people disagree (strict majority or tie with >= half other players): loose some points (50% points).
 *   - If 0 other players disagree (or minority): full points preserved.
 *   - If initially invalid word is accepted by peers without rejection: accepted (+10 / +5 points).
 * - 0 points: Invalid word or empty
 */
export function calculateStopScores(
  playerAnswers: Record<string, Record<string, string>>,
  validations: Record<string, Record<string, WordValidationResult | { isValid: boolean }>>,
  votes?: Record<string, Record<string, Record<string, boolean>>>
): Record<string, { roundScore: number; categoryScores: Record<string, number>; categoryVoteStatus?: Record<string, 'accepted' | 'rejected' | 'disputed' | 'none'> }> {
  const result: Record<string, { roundScore: number; categoryScores: Record<string, number>; categoryVoteStatus?: Record<string, 'accepted' | 'rejected' | 'disputed' | 'none'> }> = {};

  const playerIds = Object.keys(playerAnswers);

  // Initialize scores
  for (const playerId of playerIds) {
    result[playerId] = {
      roundScore: 0,
      categoryScores: {},
      categoryVoteStatus: {}
    };
  }

  // Get all categories mentioned in answers
  const allCategoryIds = new Set<string>();
  for (const playerId of playerIds) {
    const answers = playerAnswers[playerId] || {};
    for (const catId of Object.keys(answers)) {
      allCategoryIds.add(catId);
    }
  }

  // For each category, evaluate effective validity (factoring in votes) and count word frequencies
  for (const catId of allCategoryIds) {
    const wordCounts = new Map<string, number>();

    for (const playerId of playerIds) {
      const rawWord = playerAnswers[playerId]?.[catId] || '';
      const cleanWord = rawWord.trim().toLowerCase();
      const valInfo = validations[playerId]?.[catId] as WordValidationResult | undefined;
      const initialValid = valInfo?.isValid ?? false;

      // Count votes from other players only (players cannot vote on their own words)
      const otherPlayersCount = playerIds.filter(id => id !== playerId).length;
      const playerCatVotes = votes?.[playerId]?.[catId] || {};
      const voteEntries = Object.entries(playerCatVotes);
      const accepts = voteEntries.filter(([voterId, v]) => voterId !== playerId && v === true).length;
      const rejects = voteEntries.filter(([voterId, v]) => voterId !== playerId && v === false).length;

      let effectiveValid = initialValid;
      let voteStatus: 'accepted' | 'rejected' | 'disputed' | 'none' = 'none';

      if (otherPlayersCount > 0 && rejects > 0) {
        if (rejects === otherPlayersCount) {
          // Everyone disagrees: lose all points for the word
          effectiveValid = false;
          voteStatus = 'rejected';
        } else if (rejects >= otherPlayersCount / 2 && rejects >= accepts) {
          // Most people disagree: loose some points
          voteStatus = 'disputed';
        }
      } else if (!initialValid && accepts > 0 && rejects === 0) {
        // Initially invalid word accepted by peers without dispute
        effectiveValid = true;
        voteStatus = 'accepted';
      }

      if (result[playerId].categoryVoteStatus) {
        result[playerId].categoryVoteStatus![catId] = voteStatus;
      }

      if (effectiveValid && cleanWord) {
        // Group typos by correctedWord so they are correctly identified as duplicates
        const effectiveWord = (valInfo?.isTypo && valInfo?.correctedWord && voteStatus !== 'rejected')
          ? valInfo.correctedWord
          : cleanWord;
        wordCounts.set(effectiveWord, (wordCounts.get(effectiveWord) || 0) + 1);
      }
    }

    // Award scores
    for (const playerId of playerIds) {
      const rawWord = playerAnswers[playerId]?.[catId] || '';
      const cleanWord = rawWord.trim().toLowerCase();
      const valInfo = validations[playerId]?.[catId] as WordValidationResult | undefined;
      const initialValid = valInfo?.isValid ?? false;

      const voteStatus = result[playerId].categoryVoteStatus?.[catId] || 'none';
      const effectiveValid = voteStatus === 'rejected' ? false : (voteStatus === 'accepted' ? true : initialValid);

      let score = 0;
      if (effectiveValid && cleanWord) {
        const effectiveWord = (valInfo?.isTypo && valInfo?.correctedWord && voteStatus !== 'rejected')
          ? valInfo.correctedWord
          : cleanWord;
        const count = wordCounts.get(effectiveWord) || 0;
        let baseScore = count > 1 ? 5 : 10;

        // Typos and spelling mistakes receive full points (no penalty deducted), but remain flagged for review

        if (voteStatus === 'disputed') {
          // Most people disagreed: loose some points (half points)
          score = Math.floor(baseScore / 2);
        } else {
          score = baseScore;
        }
      }

      result[playerId].categoryScores[catId] = score;
      result[playerId].roundScore += score;
    }
  }

  return result;
}
